import { ActionIcon, Box, Group, NavLink, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconChevronLeft,
  IconChevronRight,
} from '@tabler/icons-react';
import {
  addEdge,
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type Connection,
  type Edge,
  type EdgeChange,
  type Node,
  type NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useState } from 'react';
import type { WorkflowDefinitionGraph, WorkflowStep } from '../api/workflows';
// Layout sizing comes from the shared canvas module so the read-only
// detail viewer and the edit canvas place nodes identically.
import {
  buildEdges,
  buildStepNodes,
  kindIcon,
  NODE_HEIGHT,
  NODE_WIDTH,
  resolveStepId,
  shouldPreservePosition,
  WORKFLOW_NODE_TYPES,
} from '../components/workflow-flow';
import {
  addLoop,
  defaultParamsForKind,
  nextOutcomeName,
  nextStepId,
  removeSteps,
  renumberOrder,
  stepsByOrder,
  type AuthorableKind,
} from './workflow-edit-helpers';
import { WorkflowEditSidePanel } from './workflow-edit-side-panel';

// MIME type used for the HTML5 drag payload when dragging from the
// palette to the canvas. Anchored to a workspace-internal value so we
// don't conflict with any external draggables (e.g. file uploads).
const DRAG_MIME = 'application/x-sa-workflow-step-kind';

interface PaletteItem {
  kind: AuthorableKind;
  label: string;
  description: string;
}

const PALETTE: PaletteItem[] = [
  {
    kind: 'gate_on_consent',
    label: 'Consent gate',
    description: 'Block the case until the required (blocking) consents are granted',
  },
  {
    kind: 'collect_questions',
    label: 'Collect questions',
    description: 'One or more questions; client posts answers',
  },
  {
    kind: 'collect_images',
    label: 'Collect images',
    description: 'Accept one of the configured image patterns',
  },
  {
    kind: 'decision',
    label: 'Decision',
    description: 'Branch on an upstream answer',
  },
  {
    kind: 'run_ai_review',
    label: 'Send for AI analysis',
    description: 'Run collected images through the AI review pipeline',
  },
  {
    kind: 'set_case_status',
    label: 'Set case status',
    description: 'Update the case status (open / awaiting histology / completed) then continue',
  },
  {
    kind: 'loop_control',
    label: 'Loop control',
    description: 'Repeat a group of steps until the client signals "add another" or "done"',
  },
  {
    kind: 'output',
    label: 'Output',
    description: 'Terminal node with the result shape',
  },
  {
    kind: 'halt',
    label: 'Halt',
    description:
      'Terminate the workflow with a reason code — the client surfaces this as a terminal state',
  },
];

/**
 * Deliberately not a bordered `Card` — nesting bordered cards inside
 * the already-bordered palette box reads as boxes-within-a-box. A
 * plain hoverable row (the same shape as the app's own sidebar
 * `NavLink`s) matches the rest of the admin UI's visual language
 * instead of inventing a one-off treatment, and lets each item's kind
 * icon match the icon shown on its canvas node once dropped.
 */
function PaletteCard({ item }: { item: PaletteItem }) {
  const Icon = kindIcon(item.kind);
  return (
    <NavLink
      label={item.label}
      description={item.description}
      leftSection={<Icon size={18} />}
      variant="light"
      draggable
      onDragStart={(e: React.DragEvent<HTMLAnchorElement>) => {
        e.dataTransfer.setData(DRAG_MIME, item.kind);
        e.dataTransfer.effectAllowed = 'move';
      }}
      style={{ cursor: 'grab', borderRadius: 6 }}
    />
  );
}

function EditorCanvas({
  definition,
  setDefinition,
  selectedId,
  onSelect,
  consentBlockingByCode,
  fullscreen,
  onToggleFullscreen,
}: {
  definition: WorkflowDefinitionGraph;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
  // Drives the selection ring on the shared workflowStep card node.
  selectedId: string | null;
  // Side-panel selection lives in the parent. React Flow tracks its
  // own internal selection for the canvas highlight; we just forward
  // node-click / pane-click events so the panel stays in sync.
  onSelect: (id: string | null) => void;
  // code → blocking map so consent-gate nodes can render
  // `code · blocking` / `code · optional` in their label.
  consentBlockingByCode?: Map<string, boolean>;
  // Whether this canvas instance is the fullscreen-modal one — drives
  // the overlaid expand/collapse icon and its aria-label/click target.
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}) {
  const reactFlow = useReactFlow();

  // React Flow owns nodes + edges state via these hooks. The hook-
  // supplied `onNodesChange`/`onEdgesChange` apply React Flow's internal
  // change events (position deltas during a drag, selection toggles,
  // dimension updates) without us having to do that bookkeeping
  // manually. Crucially: with controlled state where we rebuild
  // `nodes` ourselves on every render (the previous approach), React
  // Flow's drag tracking would get reset mid-drag — clicking a node
  // appeared to do nothing because every committed position change
  // produced a fresh node object reference, breaking the drag handle.
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Sync the canvas to the operator's definition. When the set of step
  // IDs changes (server refresh, drop-to-add, removal, etc.) we
  // recompute the nodes list, preserving the current on-canvas position
  // of any step that's already laid out and dagre-laying out the rest.
  // Selection state lives in React Flow's internal store; we don't
  // overwrite it here.
  const stepIdsKey = Object.keys(definition.steps).sort().join('|');
  useEffect(() => {
    setNodes((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      return buildStepNodes(definition, { consentBlockingByCode, selectedId }).map((node) => {
        const prevNode = prevById.get(node.id);
        if (!shouldPreservePosition(prevNode, node)) return node;
        return {
          ...node,
          position: prevNode!.position,
          // A loop's container box is resizable (see LoopGroupNode) —
          // carry over a manual resize the same way a manual drag
          // carries over above, instead of snapping back to dagre's
          // auto-fit size on every unrelated definition change.
          ...(node.type === 'loopGroup'
            ? { width: prevNode!.width ?? node.width, height: prevNode!.height ?? node.height }
            : {}),
        };
      });
    });
  }, [stepIdsKey, definition, setNodes, consentBlockingByCode, selectedId]);

  useEffect(() => {
    setEdges(buildEdges(definition));
  }, [definition, setEdges]);

  // Wrap React Flow's onNodesChange so we can also sync node removals
  // back into the definition JSON (so re-rendering after a delete
  // doesn't resurrect the step).
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      const removals = changes.filter(
        (c): c is { id: string; type: 'remove' } => c.type === 'remove',
      );
      if (removals.length === 0) return;
      // A loop's container box represents its control step (see
      // resolveStepId) — deleting the box deletes that step, not the
      // synthetic group id, which isn't a real step and removeSteps
      // wouldn't recognise.
      const removedStepIds = new Set(
        removals.map((r) => {
          const node = nodes.find((n) => n.id === r.id);
          return node ? resolveStepId(node) : r.id;
        }),
      );
      // Renumber so the order field stays contiguous after a delete.
      setDefinition(renumberOrder(removeSteps(definition, removedStepIds)));
    },
    [onNodesChange, definition, setDefinition, nodes],
  );

  const handleEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      onEdgesChange(changes);
      const removals = changes.filter(
        (c): c is { id: string; type: 'remove' } => c.type === 'remove',
      );
      if (removals.length === 0) return;
      const removedEdgeIds = new Set(removals.map((r) => r.id));
      const nextSteps: WorkflowDefinitionGraph['steps'] = {};
      for (const [sourceId, step] of Object.entries(definition.steps)) {
        const keep: Record<string, string> = {};
        for (const [outcome, target] of Object.entries(step.transitions ?? {})) {
          const edgeId = `${sourceId}-${outcome}-${target}`;
          if (!removedEdgeIds.has(edgeId)) keep[outcome] = target;
        }
        nextSteps[sourceId] = {
          ...step,
          transitions: Object.keys(keep).length ? keep : undefined,
        } as WorkflowStep;
      }
      setDefinition({ ...definition, steps: nextSteps });
    },
    [onEdgesChange, definition, setDefinition],
  );

  const onConnect = useCallback(
    (conn: Connection) => {
      if (!conn.source || !conn.target) return;
      // Auto-pick a transition outcome name (`on_complete` for non-
      // decision steps, `branch_<n>` for decisions). The side panel
      // lets the operator rename outcomes once the per-node config is
      // wired.
      const outcome = nextOutcomeName(definition, conn.source);
      const sourceStep = definition.steps[conn.source];
      if (!sourceStep) return;
      const nextSteps = { ...definition.steps };
      nextSteps[conn.source] = {
        ...sourceStep,
        transitions: {
          ...(sourceStep.transitions ?? {}),
          [outcome]: conn.target,
        },
      } as WorkflowStep;
      setDefinition({ ...definition, steps: nextSteps });
      setEdges((prev) =>
        addEdge(
          {
            id: `${conn.source}-${outcome}-${conn.target}`,
            source: conn.source as string,
            target: conn.target as string,
            label: outcome,
          },
          prev,
        ),
      );
    },
    [definition, setDefinition, setEdges],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const kind = e.dataTransfer.getData(DRAG_MIME) as AuthorableKind;
      if (!kind) return;
      // `screenToFlowPosition` already converts viewport coordinates
      // (clientX/Y) to the flow's internal coordinate space — accounting
      // for the canvas pan/zoom AND its offset within the page. Passing
      // wrapper-relative coordinates double-corrects and lands the node
      // off-screen.
      const flowPos = reactFlow.screenToFlowPosition({ x: e.clientX, y: e.clientY });
      const id = nextStepId(definition, kind);
      // Newest step lands at the end of the operator's order sequence.
      const nextOrder = stepsByOrder(definition).length + 1;
      const nextSteps: WorkflowDefinitionGraph['steps'] = {
        ...definition.steps,
        [id]: { kind, params: defaultParamsForKind(kind), order: nextOrder } as WorkflowStep,
      };
      let nextDefinition: WorkflowDefinitionGraph = { ...definition, steps: nextSteps };
      if (kind === 'loop_control') {
        nextDefinition = addLoop(nextDefinition, id);
      }
      setDefinition(nextDefinition);
      // Seed the new node's position directly so the post-effect sync
      // doesn't overwrite the cursor-relative drop with a dagre layout.
      setNodes((prev) => {
        const existingIds = new Set(prev.map((n) => n.id));
        const built = buildStepNodes(nextDefinition, { consentBlockingByCode, selectedId });
        const additions = built
          .filter((n) => !existingIds.has(n.id))
          .map((n) =>
            n.id === id
              ? {
                  ...n,
                  position: { x: flowPos.x - NODE_WIDTH / 2, y: flowPos.y - NODE_HEIGHT / 2 },
                }
              : n,
          );
        return [...prev, ...additions];
      });
    },
    [definition, reactFlow, setDefinition, setNodes, consentBlockingByCode, selectedId],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  return (
    // The wrapper holds the HTML5 drop target. It must have explicit
    // dimensions: its parent (a Mantine `<Box>`) is a plain block, not
    // a flex container, so `flex: 1` here collapsed to height 0 and
    // the drop event never fired (ReactFlow itself was visible because
    // it's absolutely positioned, but the drop layer wasn't).
    <div style={{ width: '100%', height: '100%' }} onDrop={onDrop} onDragOver={onDragOver}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={WORKFLOW_NODE_TYPES}
        onNodesChange={handleNodesChange}
        onEdgesChange={handleEdgesChange}
        onConnect={onConnect}
        // A genuine click opens the side panel; React Flow only fires
        // this when the pointer didn't move past its drag threshold,
        // so repositioning/resizing a node (reshaping the view) never
        // opens or changes the editor — only clicking it does. Clicking
        // the already-selected node toggles it closed instead of
        // re-opening the same panel.
        onNodeClick={(_, node) => {
          const clickedId = resolveStepId(node);
          onSelect(clickedId === selectedId ? null : clickedId);
        }}
        // Dragging never opens or switches the panel — that felt
        // intrusive for what's usually just tidying up the layout.
        // But leaving it open on the wrong step while a different node
        // visibly moves is also wrong, so close it (don't switch) the
        // moment a drag starts on anything other than the already-
        // selected node. Dragging the selected node itself is a no-op
        // here since the panel already matches it.
        onNodeDragStart={(_, node) => {
          const draggedId = resolveStepId(node);
          if (draggedId !== selectedId) onSelect(null);
        }}
        onPaneClick={() => onSelect(null)}
        nodesConnectable
        nodesDraggable
        elementsSelectable
        fitView
      >
        <Background />
        <Controls showInteractive={false} />
        <MiniMap pannable zoomable style={{ height: 100 }} />
        <Panel position="top-right">
          <ActionIcon
            variant="default"
            size="lg"
            aria-label={fullscreen ? 'Collapse workflow editor' : 'Expand workflow editor'}
            onClick={onToggleFullscreen}
          >
            {fullscreen ? <IconArrowsMinimize size={18} /> : <IconArrowsMaximize size={18} />}
          </ActionIcon>
        </Panel>
      </ReactFlow>
    </div>
  );
}

export interface WorkflowEditorCanvasAreaProps {
  definition: WorkflowDefinitionGraph;
  setDefinition: (next: WorkflowDefinitionGraph) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  consentBlockingByCode?: Map<string, boolean>;
  workflowOrgId: string | null;
  workflowProductId: string | null;
  // Whether this canvas instance is the fullscreen-modal one — passed
  // through to the overlaid expand/collapse icon on the canvas itself.
  fullscreen: boolean;
  onToggleFullscreen: () => void;
}

const PALETTE_WIDTH = 220;
const PALETTE_COLLAPSED_WIDTH = 40;

export function WorkflowEditorCanvasArea({
  definition,
  setDefinition,
  selectedId,
  onSelect,
  consentBlockingByCode,
  workflowOrgId,
  workflowProductId,
  fullscreen,
  onToggleFullscreen,
}: WorkflowEditorCanvasAreaProps) {
  // Independent of the fullscreen toggle above — a fresh instance mounts
  // per canvas (inline vs modal), so this simply resets to expanded
  // whenever the operator switches between them. No persistence needed.
  const [paletteOpened, { toggle: togglePalette }] = useDisclosure(true);

  // The side panel animates shut instead of vanishing when a step is
  // deselected — that needs its content to keep rendering (at the last
  // real step id) through the close transition, since `selectedId`
  // itself already goes null the instant deselection happens.
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  useEffect(() => {
    if (selectedId) setLastSelectedId(selectedId);
  }, [selectedId]);
  const sidePanelOpen = selectedId !== null;
  const sidePanelStepId = selectedId ?? lastSelectedId;

  return (
    <Group gap="md" align="stretch" style={{ flex: 1, minHeight: 0 }} wrap="nowrap">
      <Box
        w={paletteOpened ? PALETTE_WIDTH : PALETTE_COLLAPSED_WIDTH}
        style={{
          flexShrink: 0,
          overflow: 'hidden',
          border: '1px solid #ddd',
          borderRadius: 4,
          transition: 'width 200ms ease',
        }}
      >
        <Stack gap="xs" p="xs" h="100%" style={{ overflowY: paletteOpened ? 'auto' : 'hidden' }}>
          <Group justify="space-between" wrap="nowrap" gap="xs">
            {paletteOpened && (
              <Text fw={600} size="sm">
                Step palette
              </Text>
            )}
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={paletteOpened ? 'Collapse step palette' : 'Expand step palette'}
              onClick={togglePalette}
              ml={paletteOpened ? 0 : 'auto'}
              mr={paletteOpened ? 0 : 'auto'}
            >
              {paletteOpened ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
            </ActionIcon>
          </Group>
          {/* Always mounted (not conditionally) so the width collapse
              above reads as the cards sliding away rather than
              vanishing — opacity fades alongside it instead of an
              instant pop, and pointerEvents keeps it unclickable
              while hidden. */}
          <Stack
            gap="xs"
            data-testid="palette-cards"
            style={{
              opacity: paletteOpened ? 1 : 0,
              transition: 'opacity 150ms ease',
              pointerEvents: paletteOpened ? 'auto' : 'none',
            }}
          >
            <Text size="xs" c="dimmed">
              Drag onto the canvas to add a step.
            </Text>
            {PALETTE.map((p) => (
              <PaletteCard key={p.kind} item={p} />
            ))}
          </Stack>
        </Stack>
      </Box>
      <Box style={{ flex: 1, border: '1px solid #ddd', borderRadius: 4, minHeight: 0 }}>
        <ReactFlowProvider>
          <EditorCanvas
            definition={definition}
            setDefinition={setDefinition}
            selectedId={selectedId}
            onSelect={onSelect}
            consentBlockingByCode={consentBlockingByCode}
            fullscreen={fullscreen}
            onToggleFullscreen={onToggleFullscreen}
          />
        </ReactFlowProvider>
      </Box>
      <Box
        w={sidePanelOpen ? 360 : 0}
        style={{
          flexShrink: 0,
          overflow: 'hidden',
          border: '1px solid #ddd',
          borderRadius: 4,
          transition: 'width 200ms ease',
        }}
      >
        {/* Fixed inner width regardless of the outer box's animating
            width — the panel reads as sliding out from behind the
            canvas edge rather than its own content squeezing/reflowing
            as it closes. Content stays mounted (at the last real
            selection) through the close fade since `selectedId` itself
            already went null the instant deselection happened. */}
        {sidePanelStepId && (
          <div
            data-testid="side-panel-content"
            style={{
              width: 360,
              height: '100%',
              overflowY: 'auto',
              opacity: sidePanelOpen ? 1 : 0,
              transition: 'opacity 150ms ease',
            }}
          >
            <WorkflowEditSidePanel
              definition={definition}
              selectedId={sidePanelStepId}
              setDefinition={setDefinition}
              onClearSelection={() => onSelect(null)}
              workflowOrgId={workflowOrgId}
              workflowProductId={workflowProductId}
            />
          </div>
        )}
      </Box>
    </Group>
  );
}
